import { supabase } from './supabase'
// Serviços removidos - não utilizados no sistema atual

export async function testSupabaseConnection() {
  console.log('🔍 Testando conexão com Supabase...')
  
  try {
    // Teste 1: Verificar se consegue conectar
    const { error: healthError } = await supabase
      .from('clientes')
      .select('count')
      .limit(1)
    
    if (healthError) {
      console.error('❌ Erro na conexão:', healthError.message)
      return { success: false, error: healthError.message }
    }

    console.log('✅ Conexão estabelecida com sucesso!')

    // Teste 2: Verificar estrutura das tabelas
    console.log('🔍 Verificando estrutura das tabelas...')
    
    const tables = ['clientes', 'livros', 'pedidos']
    const tableStatus: Record<string, any> = {}

    for (const table of tables) {
      try {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .limit(1)
        
        if (error) {
          console.log(`❌ Tabela '${table}': ${error.message}`)
          tableStatus[table] = { exists: false, error: error.message }
        } else {
          console.log(`✅ Tabela '${table}': OK`)
          tableStatus[table] = { exists: true, sampleData: data }
        }
      } catch (err) {
        console.log(`❌ Tabela '${table}': Erro inesperado`)
        tableStatus[table] = { exists: false, error: 'Erro inesperado' }
      }
    }

    // Teste 3: Verificar autenticação
    console.log('🔍 Testando sistema de autenticação...')
    
    const { data: { session } } = await supabase.auth.getSession()
    console.log('📋 Sessão atual:', session ? 'Ativa' : 'Nenhuma')

    return {
      success: true,
      connection: 'OK',
      tables: tableStatus,
      auth: {
        session: session ? 'active' : 'none',
        user: session?.user?.email || null
      }
    }

  } catch (error) {
    console.error('❌ Erro geral:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido' 
    }
  }
}

// Função para criar as tabelas se não existirem
export async function createTablesIfNotExist() {
  console.log('🏗️ Verificando e criando tabelas necessárias...')
  
  const createClientesTable = `
    CREATE TABLE IF NOT EXISTS clientes (
      id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
      email VARCHAR(255) UNIQUE NOT NULL,
      nome VARCHAR(255),
      telefone VARCHAR(20),
      created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
      updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
    );
  `

  const createLivrosTable = `
    CREATE TABLE IF NOT EXISTS livros (
      id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
      titulo VARCHAR(255) NOT NULL,
      subtitulo TEXT,
      cover_url TEXT,
      download_url TEXT,
      status VARCHAR(50) DEFAULT 'pending',
      cliente_id UUID REFERENCES clientes(id),
      created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
      updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
    );
  `

  const createPedidosTable = `
    CREATE TABLE IF NOT EXISTS pedidos (
      id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
      cliente_id UUID REFERENCES clientes(id),
      livro_id UUID REFERENCES livros(id),
      status VARCHAR(50) DEFAULT 'pending',
      valor DECIMAL(10,2),
      servicos JSONB,
      created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
      updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
    );
  `

  try {
    // Nota: Em um ambiente real, você executaria essas queries via SQL Editor do Supabase
    // ou usando migrações. Aqui é apenas para documentação.
    console.log('📋 Scripts SQL para criar as tabelas:')
    console.log('1. Tabela clientes:', createClientesTable)
    console.log('2. Tabela livros:', createLivrosTable)
    console.log('3. Tabela pedidos:', createPedidosTable)
    
    return {
      success: true,
      message: 'Scripts SQL gerados. Execute-os no SQL Editor do Supabase.'
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido'
    }
  }
}

// Função para mapear a estrutura real das tabelas (APENAS LEITURA)
export async function mapDatabaseStructure() {
  console.log('🔍 Mapeando estrutura real do banco de dados...')

  const structure = {
    clientes: { columns: [], sample: null, count: 0 },
    pedidos: { columns: [], sample: null, count: 0 },
    livros: { columns: [], sample: null, count: 0 }
  }

  try {
    // 1. Mapear estrutura da tabela clientes
    console.log('📋 Analisando tabela clientes...')
    const { data: clientesSample, error: errorClientes } = await supabase
      .from('clientes')
      .select('*')
      .limit(1)

    if (!errorClientes && clientesSample && clientesSample.length > 0) {
      structure.clientes.columns = Object.keys(clientesSample[0]) as any
      structure.clientes.sample = clientesSample[0]
    }

    const { count: clientesCount } = await supabase
      .from('clientes')
      .select('*', { count: 'exact', head: true })
    structure.clientes.count = clientesCount || 0

    // 2. Mapear estrutura da tabela pedidos
    console.log('📋 Analisando tabela pedidos...')
    const { data: pedidosSample, error: errorPedidos } = await supabase
      .from('pedidos')
      .select('*')
      .limit(1)

    if (!errorPedidos && pedidosSample && pedidosSample.length > 0) {
      structure.pedidos.columns = Object.keys(pedidosSample[0]) as any
      structure.pedidos.sample = pedidosSample[0]
    }

    const { count: pedidosCount } = await supabase
      .from('pedidos')
      .select('*', { count: 'exact', head: true })
    structure.pedidos.count = pedidosCount || 0

    // 3. Mapear estrutura da tabela livros
    console.log('📋 Analisando tabela livros...')
    const { data: livrosSample, error: errorLivros } = await supabase
      .from('livros')
      .select('*')
      .limit(1)

    if (!errorLivros && livrosSample && livrosSample.length > 0) {
      structure.livros.columns = Object.keys(livrosSample[0]) as any
      structure.livros.sample = livrosSample[0]
    }

    const { count: livrosCount } = await supabase
      .from('livros')
      .select('*', { count: 'exact', head: true })
    structure.livros.count = livrosCount || 0

    console.log('✅ Estrutura mapeada com sucesso!')
    return { success: true, structure }

  } catch (error) {
    console.error('❌ Erro ao mapear estrutura:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido',
      structure
    }
  }
}

// Função para testar os serviços (DESABILITADA - serviços removidos)
export async function testServices() {
  return { message: 'Serviços legados removidos' }
  /*
  console.log('🧪 Testando serviços...')

  const results = {
    clientes: { success: false, error: null as any },
    livros: { success: false, error: null as any },
    pedidos: { success: false, error: null as any },
    auth: { success: false, error: null as any }
  }

  try {
    // Teste 1: Serviço de clientes
    console.log('🔍 Testando serviço de clientes...')
    const { data: clientes, error: errorClientes } = await clientesService.list(5)
    if (errorClientes) {
      results.clientes.error = errorClientes.message
      console.log('❌ Serviço de clientes:', errorClientes.message)
    } else {
      results.clientes.success = true
      console.log('✅ Serviço de clientes: OK', `(${clientes?.length || 0} registros)`)
    }

    // Teste 2: Serviço de livros
    console.log('🔍 Testando serviço de livros...')
    const { data: livros, error: errorLivros } = await livrosService.list(5)
    if (errorLivros) {
      results.livros.error = errorLivros.message
      console.log('❌ Serviço de livros:', errorLivros.message)
    } else {
      results.livros.success = true
      console.log('✅ Serviço de livros: OK', `(${livros?.length || 0} registros)`)
    }

    // Teste 3: Serviço de pedidos
    console.log('🔍 Testando serviço de pedidos...')
    const { data: pedidos, error: errorPedidos } = await pedidosService.list(5)
    if (errorPedidos) {
      results.pedidos.error = errorPedidos.message
      console.log('❌ Serviço de pedidos:', errorPedidos.message)
    } else {
      results.pedidos.success = true
      console.log('✅ Serviço de pedidos: OK', `(${pedidos?.length || 0} registros)`)
    }

    // Teste 4: Serviço de autenticação
    console.log('🔍 Testando serviço de autenticação...')
    const { session } = await authService.getSession()
    results.auth.success = true
    console.log('✅ Serviço de autenticação: OK', session ? 'Sessão ativa' : 'Sem sessão')

    return {
      success: true,
      results
    }

  } catch (error) {
    console.error('❌ Erro geral nos testes:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido',
      results
    }
  }
  */
}
